/**
 * Versions publiées d'une animation (lot 5) : chacune est immuable (0666).
 * « Reprendre dans le brouillon » remet son document dans le brouillon (pour
 * revenir en arrière après une mauvaise modification) ; la version en ligne
 * ne change qu'à la publication suivante.
 */

import { useState } from 'react';
import { History } from 'lucide-react';
import { Button } from '@jeraisonne/ui';
import {
  useAnimationVersions,
  useRestoreAnimationVersion,
  type AnimationRow,
} from '@/lib/animations/api';

const dateFmt = new Intl.DateTimeFormat('fr-FR', {
  day: 'numeric',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
});

export function AnimationVersions({
  row,
  toolIds,
}: {
  row: Pick<AnimationRow, 'id' | 'status' | 'published_version'>;
  toolIds?: readonly string[];
}) {
  const versions = useAnimationVersions(row.id, row.published_version != null);
  const restore = useRestoreAnimationVersion();
  const [confirm, setConfirm] = useState<number | null>(null);
  const [done, setDone] = useState<number | null>(null);
  const list = versions.data ?? [];
  if (row.published_version == null || list.length === 0) return null;

  return (
    <div className="space-y-2">
      <ul className="divide-ink-100 border-ink-200 divide-y rounded-xl border bg-white">
        {list.map((v) => {
          const live = row.status === 'published' && row.published_version === v.version_no;
          return (
            <li key={v.version_no} className="flex flex-wrap items-center gap-2 px-3 py-2 text-sm">
              <History className="text-ink-400 h-4 w-4" aria-hidden />
              <span className="text-ink-900 font-semibold">Version {v.version_no}</span>
              <span className="text-ink-500">{dateFmt.format(new Date(v.published_at))}</span>
              {live && (
                <span className="bg-success-50 text-success-600 rounded-full px-2 py-0.5 text-[12px] font-semibold">
                  en ligne
                </span>
              )}
              <span className="ml-auto">
                {confirm === v.version_no ? (
                  <span className="flex flex-wrap items-center gap-2 text-[13px]">
                    Le brouillon actuel sera remplacé.
                    <Button
                      size="sm"
                      loading={restore.isPending}
                      onClick={() =>
                        restore.mutate(
                          { id: row.id, versionNo: v.version_no, toolIds },
                          {
                            onSuccess: (n) => {
                              setDone(n);
                              setConfirm(null);
                            },
                          },
                        )
                      }
                    >
                      Remplacer
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setConfirm(null)}>
                      Non
                    </Button>
                  </span>
                ) : (
                  <Button size="sm" variant="outline" onClick={() => setConfirm(v.version_no)}>
                    Reprendre dans le brouillon
                  </Button>
                )}
              </span>
            </li>
          );
        })}
      </ul>
      {done !== null && (
        <p role="status" className="text-success-600 text-[13px] font-semibold">
          Le brouillon est la version {done}. Relis-le, puis republie pour la remettre en ligne.
        </p>
      )}
      {restore.error && (
        <p role="alert" className="text-error-600 text-[13px]">
          {restore.error.message}
        </p>
      )}
    </div>
  );
}
